import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Button,
  Col,
  Container,
  Dropdown,
  Form,
  Row,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FloatInput, showToast } from "src/componentes";
import { MenuDropdownToggle } from "src/componentes/MenuDropdownToggle";
import { getLocal, NFeDto, STORAGENFE_KEY, updateLocal } from "../../NFe.Dto";
import { useNavigate } from "react-router-dom";
import {
  NFeExcluir,
  NFeFiltrar,
  NFeIncluir,
} from "src/services/Fiscal.Service";
import {
  MapearErrosServidor,
  MapearErrosServidorCampo,
} from "src/hook/Functions";
import moment from "moment";
import { showMessageBox } from "src/componentes/MessageBox/Messagebox";
import {
  FormType,
  InformacoesAdicionaisSchema,
} from "./NFe.Cadastro.InformacoesAdicionais.Dto";

const NFeCadastroInformacoesAdicionaisView = () => {
  const navigate = useNavigate();

  const nf = getLocal<NFeDto>(STORAGENFE_KEY);
  const informacoesAdicionais = nf?.informacoesAdicionais;
  const [numeroNF, setNumeroNF] = useState<number | undefined>(
    nf?.numeroNF && nf.numeroNF > 0 ? nf.numeroNF : undefined
  );

  const handleVoltar = () => () => {
    navigate("../volumes");
  };

  const form = useForm<FormType>({
    resolver: yupResolver(InformacoesAdicionaisSchema),
    defaultValues: {
      informacoesAdicionaisFisco:
        informacoesAdicionais?.informacoesAdicionaisFisco,
      informacoesComplementaresContribuinte:
        informacoesAdicionais?.informacoesComplementaresContribuinte,
      emissaoDataHora: nf?.emissaoDataHora
        ? moment(nf.emissaoDataHora).format("DD/MM/YYYY HH:mm:ss")
        : moment().format("DD/MM/YYYY HH:mm:ss"),
      saidaEntradaDataHora: nf?.saidaEntradaDataHora
        ? moment(nf.saidaEntradaDataHora).format("DD/MM/YYYY HH:mm:ss")
        : null,
      numeroNF: nf?.numeroNF && nf.numeroNF > 0 ? nf.numeroNF : undefined,
    },
  });

  const handleAlterarNumeroNFe = () => {
    const input = prompt(
      "Informe o número da NF-e (deixe em branco para sequência automática):",
      numeroNF ? String(numeroNF) : ""
    );

    if (input === null) {
      return;
    }

    const trimmed = input.trim();
    if (!trimmed) {
      setNumeroNF(undefined);
      form.setValue("numeroNF", null as any);
      updateLocal<NFeDto>(STORAGENFE_KEY, {
        numeroNF: 0,
      });
      showToast({
        message: "A numeração da NF-e seguirá a sequência automática.",
        title: "Número da NF-e",
        variant: "Info",
      });
      return;
    }

    const valor = Number(trimmed);
    if (!/^\d+$/.test(trimmed) || isNaN(valor) || valor <= 0) {
      showToast({
        message:
          "Número de NF-e inválido! Por favor, digite um número inteiro positivo.",
        title: "Validação",
        variant: "Danger",
      });
      return;
    }

    setNumeroNF(valor);
    form.setValue("numeroNF", valor);
    updateLocal<NFeDto>(STORAGENFE_KEY, {
      numeroNF: valor,
    });
    showToast({
      message: `Será atribuído o número ${valor} para a NF-e.`,
      title: "Número da NF-e",
      variant: "Success",
    });
  };

  const handleRemoverNumeroNFe = () => {
    setNumeroNF(undefined);
    form.setValue("numeroNF", null as any);
    updateLocal<NFeDto>(STORAGENFE_KEY, {
      numeroNF: 0,
    });
    showToast({
      message: "A numeração da NF-e seguirá a sequência automática.",
      title: "Número da NF-e",
      variant: "Info",
    });
  };

  const Yes = async (data: FormType) => {
    const finalNumeroNF = numeroNF ? Number(numeroNF) : 0;

    updateLocal<NFeDto>(STORAGENFE_KEY, {
      numeroNF: finalNumeroNF,
      informacoesAdicionais: {
        informacoesAdicionaisFisco: data.informacoesAdicionaisFisco ?? "",
        informacoesComplementaresContribuinte:
          data.informacoesComplementaresContribuinte ?? "",
      },
      emissaoDataHora: moment(
        data.emissaoDataHora,
        "DD/MM/YYYY HH:mm:ss",
      ).format("YYYY-MM-DDTHH:mm:ss"),
      saidaEntradaDataHora: data.saidaEntradaDataHora
        ? moment(data.saidaEntradaDataHora, "DD/MM/YYYY HH:mm:ss").format(
            "YYYY-MM-DDTHH:mm:ss",
          )
        : null,
    });

    const nf = getLocal<NFeDto>(STORAGENFE_KEY);
    showToast({
      message: "Inserindo...",
      title: "Em cadastro",
      variant: "Info",
    });

    if (nf?.id) {
      const nfeExiste = await NFeFiltrar({
        id: nf.id,
      });
      if (nfeExiste.data.success && nfeExiste.data.data.length > 0) {
        const response = await NFeExcluir(nf.id);
        if (!response.data.success) {
          MapearErrosServidor(response.data.notifications, showToast);
          return;
        }
      }
    }

    const response = await NFeIncluir(nf!);

    if (response.data.success) {
      navigate("/fiscal/nfe");
    } else {
      const notifications = response.data.notifications;
      notifications.forEach((item) => {
        item.key = item.key.replace("InformacoesAdicionais.", "");
      });

      MapearErrosServidorCampo(notifications, form.setError);
      response.data.notifications.forEach((item) => {
        showToast({
          message: item.key + ": " + item.message,
          title: "Erro",
          variant: "Danger",
        });
      });
    }
  };

  const onSubmit = async (data: FormType, e: any) => {
    const mensagemConfirmacao = numeroNF
      ? `Deseja confirmar a NF-e com o número ${numeroNF}?`
      : "Deseja confirmar a NFe?";

    showMessageBox(mensagemConfirmacao, "Atenção!", "YesNo", "Question", {
      onYes: async () => {
        await Yes(data);
      },
    });
  };

  return (
    <>
      <Form noValidate onSubmit={form.handleSubmit(onSubmit)}>
        <Row className="justify-content-end mb-2">
          <Col xs="auto">
            <Dropdown align="end">
              <MenuDropdownToggle>
                <FontAwesomeIcon icon={faEllipsisV} />
              </MenuDropdownToggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleAlterarNumeroNFe}>
                  Alterar número da NF-e
                </Dropdown.Item>
                {numeroNF && numeroNF > 0 ? (
                  <Dropdown.Item onClick={handleRemoverNumeroNFe}>
                    Usar sequência automática
                  </Dropdown.Item>
                ) : null}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>

        {numeroNF && numeroNF > 0 ? (
          <Row className="mb-3">
            <Col xs={12}>
              <Alert
                variant="info"
                className="d-flex align-items-center py-2 mb-0"
              >
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                <span>
                  Número da NF-e vinculado: <strong>{numeroNF}</strong> (será
                  atribuído à nota)
                </span>
              </Alert>
            </Col>
          </Row>
        ) : null}

        <Row>
          <Col xs={12}>
            <FloatInput
              label="Data Emissão"
              inputMask="datahora"
              registerField={form.register("emissaoDataHora")}
              errorsMessage={form.formState.errors.emissaoDataHora}
            />
          </Col>
          <Col xs={12} className="mt-3">
            <FloatInput
              label="Entrada/Saída"
              inputMask="datahora"
              registerField={form.register("saidaEntradaDataHora")}
            />
          </Col>
          <Col xs={12} className="mt-3">
            <FloatInput
              label="Informações Adicionais"
              inputMask="textarea"
              maxLength={2000}
              registerField={form.register(
                "informacoesComplementaresContribuinte",
              )}
              errorsMessage={
                form.formState.errors.informacoesComplementaresContribuinte
              }
            />
          </Col>
          <Col xs={12} className="mt-3">
            <FloatInput
              label="Informações do Fisco"
              inputMask="textarea"
              maxLength={2000}
              registerField={form.register("informacoesAdicionaisFisco")}
              errorsMessage={form.formState.errors.informacoesAdicionaisFisco}
            />
          </Col>
        </Row>
        <div className="fixed-bottom">
          <Container className="py-3">
            <Row>
              <Col xs={6} className="d-grid">
                <Button variant="outline-secondary" onClick={handleVoltar()}>
                  Voltar
                </Button>
              </Col>
              <Col xs={6} className="d-grid">
                <Button variant="outline-primary" type="submit">
                  Próximo
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Form>
    </>
  );
};

export default NFeCadastroInformacoesAdicionaisView;
